import { useCallback, useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { Select } from 'foundations-components/transitional/components/Select';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { isJourneyReport } from 'reporting-data/tsTypes/reportTypes';
import { DEAL_JOURNEY_ANALYTICS_READ, JOURNEY_ANALYTICS_READ_SCOPE } from 'reporting-data/constants/scopes';
import { FETCH_LISTS_BY_IDS, FETCH_LISTS_BY_NAME, FETCH_OBJECT_LISTS_QUERY } from '../../../api/lists';
import { getJourneyDataTypeName, getObjectListFilterValue, getJourneyObjectTypeId, getAllowAnonymousVisitors } from '../common/journeyHelpers';
import { PAGE_SIZE, mapAsyncResultsToOptions, buildObjectListFilterGroup } from './utils';
import { fromJS } from 'immutable';
import { constructNewReportFiltering } from '../JourneyPropertyFilterEditor/utils';
import ErrorBoundary from 'reporting-action-components/utils/ErrorBoundary';
import { Text } from 'foundations-components/components/Text';
import { Small } from 'foundations-components/transitional/components/Small';
import { View } from 'foundations-components/transitional/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ErrorComponent = () => /*#__PURE__*/_jsx(Text, {
  as: "small",
  use: "error",
  children: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "reporting-enablement.reportEditors.journeyListFilter.error"
  })
});
const MAX_NUMBER_OF_LISTS = 5;
export const useObjectListOptions = (objectId, currentValue) => {
  const [savedOptions, setSavedOptions] = useState([]);
  const {
    fetchMore
  } = useQuery(FETCH_OBJECT_LISTS_QUERY, {
    variables: {
      currentObject: objectId,
      after: null,
      pageSize: 0
    }
  });
  const {
    fetchMore: fetchMoreSearchResults
  } = useQuery(FETCH_LISTS_BY_NAME, {
    variables: {
      name: ''
    }
  });
  const {
    data: savedLists
  } = useQuery(FETCH_LISTS_BY_IDS, {
    variables: {
      listIds: [currentValue]
    }
  });
  useEffect(() => {
    if (savedLists) {
      setSavedOptions(savedLists.listOverview.edges.map(edge => ({
        text: edge.node.name,
        value: edge.node.id
      })));
    }
  }, [savedLists]);
  const loadListOptions = async (input, callback, pagination = {
    after: null,
    pageSize: PAGE_SIZE
  }) => {
    if (input) {
      const response = await fetchMoreSearchResults({
        variables: {
          name: input,
          pagination
        }
      });
      mapAsyncResultsToOptions({
        callback,
        response
      });
    }
    const response = await fetchMore({
      variables: pagination
    });
    mapAsyncResultsToOptions({
      callback,
      response
    });
  };
  return {
    savedOptions,
    loadListOptions
  };
};
export const JourneyListFilterSelect = ({
  objectId,
  onChange,
  value,
  disabled = false
}) => {
  const {
    savedOptions,
    loadListOptions
  } = useObjectListOptions(objectId, value);
  return /*#__PURE__*/_jsxs(View, {
    children: [value && value.length >= MAX_NUMBER_OF_LISTS && /*#__PURE__*/_jsx(Small, {
      use: "error",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "reporting-enablement.reportEditors.journeyListFilter.limitMessage",
        options: {
          maxNumLists: MAX_NUMBER_OF_LISTS
        }
      })
    }), /*#__PURE__*/_jsx(Select, {
      multi: true,
      "data-test-id": "journey-list-filter",
      anchorType: "combined",
      options: savedOptions,
      disabled: disabled,
      clearable: true,
      onChange: ({
        target: {
          value: nextValue
        }
      }) => {
        if (nextValue && nextValue.length <= MAX_NUMBER_OF_LISTS) {
          onChange(nextValue);
        }
      },
      value: value,
      loadOptions: loadListOptions,
      placeholder: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "settingLabels.list.placeholder"
      })
    })]
  });
};
const JourneyListFilterEditor = ({
  report,
  onChange
}) => {
  const journeyReport = report.toJS();
  const objectName = getJourneyDataTypeName(journeyReport);
  const objectId = getJourneyObjectTypeId(journeyReport);
  const value = getObjectListFilterValue(journeyReport);
  const disabled = getAllowAnonymousVisitors(journeyReport);
  const updateReport = useCallback(nextList => {
    const objectListFilterGroup = nextList ? buildObjectListFilterGroup(nextList, objectName) : undefined;
    const updatedReport = report.setIn(['journeyQuery', 'objectQuery', 'filtering'], fromJS(constructNewReportFiltering(report.toJS(), {
      objectList: objectListFilterGroup,
      deleteList: !objectListFilterGroup
    })));
    onChange(updatedReport);
  }, [report, objectName, onChange]);
  return /*#__PURE__*/_jsx(FormControl, {
    label: /*#__PURE__*/_jsx(FormattedMessage, {
      message: `settingLabels.list.label.${objectName}`
    }),
    children: /*#__PURE__*/_jsx(ErrorBoundary, {
      ErrorComponent: ErrorComponent,
      children: /*#__PURE__*/_jsx(JourneyListFilterSelect, {
        value: value,
        objectId: objectId,
        onChange: updateReport,
        disabled: disabled
      })
    })
  });
};
JourneyListFilterEditor.isUsableForReport = (report, gates, scopes) => isJourneyReport(report) && (scopes.includes(DEAL_JOURNEY_ANALYTICS_READ) || scopes.includes(JOURNEY_ANALYTICS_READ_SCOPE));
export default JourneyListFilterEditor;